import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatIDR } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { useAuth } from "@/contexts/AuthContext";
import { useConfig } from "@/contexts/ConfigContext";
import { canDeleteRecord } from "@/lib/perms";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { KanbanSquare, Trash2, MoveRight, Clock, ArrowDownAZ, ArrowUpAZ, Wallet } from "lucide-react";
import { toast } from "sonner";
import DealComments from "@/components/DealComments";
import ProgressLog from "@/components/ProgressLog";
import PipelineToolbar from "@/components/PipelineToolbar";
import DealFormFields from "@/components/DealFormFields";
import PnlPanel from "@/components/PnlPanel";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

const emptyForm = {
  title: "", client_id: "", stage: "information", value: 0, probability: 20,
  expected_spk_date: "", expected_completion_date: "", service: "", scope: "",
};

const probTone = (p) => {
  if (p >= 75) return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (p >= 40) return "bg-amber-50 text-amber-700 border-amber-200";
  return "bg-slate-100 text-slate-600 border-slate-200";
};

const Pipeline = () => {
  const { t } = useLang();
  const { can, user } = useAuth();
  const cfg = useConfig();
  const [params, setParams] = useSearchParams();
  const canWrite = can("deals:write");
  const canStage = can("deals:stage") || can("deals:stage_request");
  const canAssignPic = can("deals:assign_pic");
  const needsApproval = can("deals:stage_request") && !can("deals:stage");

  const [deals, setDeals] = useState([]);
  const [clients, setClients] = useState([]);
  const [picOptions, setPicOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("detail");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const stageKeys = useMemo(() => cfg?.stageKeys || [], [cfg?.stageKeys]);
  const query = params.get("q") || "";
  const picFilter = params.get("pic") || "all";
  const sortField = params.get("sort") || "stage";
  const sortDir = params.get("dir") || "asc";
  const stageParam = params.get("stages");
  const visibleStages = useMemo(
    () => new Set(
      stageParam === null ? stageKeys : stageParam.split(",").filter((s) => s && s !== "none")
    ),
    [stageParam, stageKeys]
  );

  const setParam = useCallback((patch) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(patch).forEach(([k, v]) => {
        if (v === null || v === "" || v === undefined) next.delete(k);
        else next.set(k, v);
      });
      return next;
    }, { replace: true });
  }, [setParams]);

  const load = useCallback(async () => {
    try {
      const [d, c, p] = await Promise.all([api.get("/deals"), api.get("/clients"), api.get("/team/pic-options")]);
      setDeals(d.data);
      setClients(c.data);
      setPicOptions(p.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat data pipeline");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const clientMap = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c])), [clients]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = deals.filter((d) => {
      if (!visibleStages.has(d.stage)) return false;
      if (picFilter !== "all" && d.pic_id !== picFilter) return false;
      if (!q) return true;
      const hay = `${d.title} ${clientMap[d.client_id]?.company || ""} ${d.service || ""} ${d.scope || ""}`.toLowerCase();
      return hay.includes(q);
    });
    const rank = (d) => stageKeys.indexOf(d.stage);
    const key = (d) => {
      switch (sortField) {
        case "title": return (d.title || "").toLowerCase();
        case "value": return Number(d.value || 0);
        case "probability": return Number(d.probability || 0);
        case "expected_spk_date": return d.expected_spk_date || "9999-12-31";
        case "expected_completion_date": return d.expected_completion_date || "9999-12-31";
        default: return rank(d);
      }
    };
    rows.sort((a, b) => {
      const ka = key(a), kb = key(b);
      if (ka === kb) return 0;
      return (ka > kb ? 1 : -1) * (sortDir === "asc" ? 1 : -1);
    });
    return rows;
  }, [deals, visibleStages, picFilter, query, sortField, sortDir, stageKeys, clientMap]);

  const totals = useMemo(() => ({
    value: filtered.reduce((s, d) => s + Number(d.value || 0), 0),
    profit: filtered.reduce(
      (s, d) => s + (d.pnl_summary?.expense_count ? Number(d.pnl_summary.net_profit || 0) : 0), 0
    ),
  }), [filtered]);

  const openNew = () => { setEditing(null); setForm(emptyForm); setTab("detail"); setOpen(true); };
  const openEdit = (d) => {
    setEditing(d);
    setForm({
      ...emptyForm, ...d,
      expected_spk_date: d.expected_spk_date || d.expected_close_date || "",
      expected_completion_date: d.expected_completion_date || "",
      service: d.service || "",
    });
    setTab("detail");
    setOpen(true);
  };

  const submit = async () => {
    if (!form.title || !form.client_id) { toast.error("Judul & Klien wajib"); return; }
    const payload = {
      title: form.title, client_id: form.client_id, stage: form.stage,
      value: Number(form.value) || 0, probability: Number(form.probability) || 0,
      expected_close_date: form.expected_spk_date, expected_spk_date: form.expected_spk_date,
      expected_completion_date: form.expected_completion_date, service: form.service, scope: form.scope,
    };
    try {
      if (editing) {
        const res = await api.put(`/deals/${editing.id}`, payload);
        toast.success(res.data?.pending_stage ? t("stage_request_sent") : t("save"));
      } else {
        await api.post("/deals", payload);
        toast.success(t("save"));
      }
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const moveStage = async (id, stage) => {
    try {
      const res = await api.put(`/deals/${id}`, { stage });
      toast.success(res.data?.pending_stage ? t("stage_request_sent") : t("save"));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const changePic = async (dealId, picId) => {
    try {
      await api.put(`/deals/${dealId}/pic`, { pic_id: picId });
      toast.success(t("pic_updated"));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const del = async (id) => {
    if (!window.confirm(t("delete_deal_confirm"))) return;
    try {
      await api.delete(`/deals/${id}`);
      toast.success(t("delete"));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const dialogTitle = () => {
    if (!editing) return t("new_deal");
    return canWrite ? t("edit") : editing.title;
  };

  const toggleStage = (stage) => {
    const next = new Set(visibleStages);
    if (next.has(stage)) next.delete(stage); else next.add(stage);
    setParam({ stages: next.size === stageKeys.length ? null : [...next].join(",") });
  };
  const toggleSort = () => setParam({ dir: sortDir === "asc" ? "desc" : "asc" });

  return (
    <div className="space-y-6" data-testid="deal-pipeline-view">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("pipeline")}</h2>
          <p className="text-sm text-slate-500 mt-1">
            {filtered.length} / {deals.length} {t("deals").toLowerCase()} · <span className="mono font-semibold text-slate-700">{formatIDR(totals.value)}</span>
            {totals.profit !== 0 && (
              <span data-testid="pipeline-total-profit"> · {t("est_profit")}: <span className="mono font-semibold text-emerald-700">{formatIDR(totals.profit)}</span></span>
            )}
          </p>
        </div>
        <PipelineToolbar
          visibleStages={visibleStages}
          onToggleStage={toggleStage}
          onSelectAllStages={() => setParam({ stages: null })}
          onClearStages={() => setParam({ stages: "none" })}
          sortField={sortField}
          sortDir={sortDir}
          onSortField={(v) => setParam({ sort: v === "stage" ? null : v })}
          onToggleSort={toggleSort}
          query={query}
          onQuery={(v) => setParam({ q: v })}
          picFilter={picFilter}
          onPicFilter={(v) => setParam({ pic: v === "all" ? null : v })}
          picOptions={picOptions}
          canWrite={canWrite}
          canAdd={clients.length > 0}
          onAdd={openNew}
          onReset={() => setParams({}, { replace: true })}
        />
      </div>

      {!canWrite && !canStage && (
        <Card className="p-3 border-slate-200 bg-slate-50 text-slate-600 text-xs" data-testid="read-only-notice">
          {t("read_only_notice")}
        </Card>
      )}

      {canWrite && clients.length === 0 && (
        <Card className="p-4 border-amber-200 bg-amber-50 text-amber-900 text-sm">
          Tambahkan klien terlebih dahulu di menu <b>{t("clients")}</b> sebelum membuat deal.
        </Card>
      )}

      {filtered.length === 0 ? (
        <Card className="p-12 text-center text-slate-400 border-dashed">
          <KanbanSquare className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p className="text-sm">{deals.length === 0 ? t("empty_deals") : t("no_matching_deals")}</p>
        </Card>
      ) : (
        <Card className="border-slate-200 overflow-hidden shadow-sm rounded-2xl" data-testid="pipeline-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/80 border-b border-slate-200">
                <tr className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                  <th className="px-4 py-3">{t("title")}</th>
                  <th className="px-4 py-3">{t("client")}</th>
                  <th className="px-4 py-3 w-52">
                    <button
                      onClick={() => { setParam({ sort: null }); toggleSort(); }}
                      data-testid="stage-column-sort"
                      className="inline-flex items-center gap-1 uppercase tracking-wider font-semibold text-slate-500 hover:text-slate-900"
                    >
                      {t("stage")}
                      {sortDir === "asc" ? <ArrowDownAZ className="w-3.5 h-3.5" /> : <ArrowUpAZ className="w-3.5 h-3.5" />}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-right">{t("value")}</th>
                  <th className="px-4 py-3 text-right">{t("est_profit")}</th>
                  <th className="px-4 py-3 text-center">%</th>
                  <th className="px-4 py-3">{t("expected_spk")}</th>
                  <th className="px-4 py-3">{t("expected_completion")}</th>
                  <th className="px-4 py-3 w-44">{t("pic")}</th>
                  <th className="px-4 py-3 text-right w-24"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((d, i) => {
                  const st = cfg.stageStyle(d.stage);
                  const canDelete = canDeleteRecord(user, d, "pic_id");
                  return (
                    <motion.tr
                      key={d.id}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: Math.min(i * 0.015, 0.2), duration: 0.22 }}
                      data-testid={`deal-row-${d.id}`}
                      className="border-b border-slate-100 hover:bg-orange-50/30 cursor-pointer transition-colors"
                      onClick={() => openEdit(d)}
                    >
                      <td className="px-4 py-3 font-medium text-slate-900">
                        {d.title}
                        {d.service && <div className="text-[11px] text-slate-400 font-normal">{d.service}</div>}
                      </td>
                      <td className="px-4 py-3 text-slate-600">{clientMap[d.client_id]?.company || "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold border ${st.bg} ${st.border} ${st.text}`}>
                          <span className={`stage-dot ${st.dot}`}></span>{cfg.stageLabel(d.stage)}
                        </span>
                        {d.pending_stage && (
                          <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5" data-testid={`pending-badge-${d.id}`}>
                            <Clock className="w-3 h-3" /> {t("awaiting_approval")}: {cfg.stageLabel(d.pending_stage)}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 mono font-semibold text-slate-900 text-right whitespace-nowrap">{formatIDR(d.value)}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap" data-testid={`deal-profit-${d.id}`}>
                        {d.pnl_access && d.pnl_summary?.expense_count ? (
                          <span className={`mono text-xs font-semibold ${Number(d.pnl_summary?.net_profit) >= 0 ? "text-emerald-700" : "text-rose-600"}`}>
                            {formatIDR(d.pnl_summary?.net_profit || 0)}
                          </span>
                        ) : <span className="text-slate-300 text-xs">{d.pnl_access ? "—" : "•••"}</span>}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold border ${probTone(d.probability)}`} data-testid={`prob-chip-${d.id}`}>
                          {d.probability}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-500 whitespace-nowrap text-xs">{d.expected_spk_date || d.expected_close_date || "—"}</td>
                      <td className="px-4 py-3 text-slate-500 whitespace-nowrap text-xs">{d.expected_completion_date || "—"}</td>
                      <td className="px-4 py-3 text-slate-500 text-xs" onClick={(e) => canAssignPic && e.stopPropagation()}>
                        {canAssignPic ? (
                          <Select value={d.pic_id || ""} onValueChange={(v) => changePic(d.id, v)}>
                            <SelectTrigger data-testid={`pic-select-${d.id}`} className="h-8 text-xs bg-white">
                              <SelectValue placeholder={t("pic")} />
                            </SelectTrigger>
                            <SelectContent>
                              {picOptions.map((p) => (
                                <SelectItem key={p.user_id} value={p.user_id}>{p.name} · {t(p.role)}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <span data-testid={`pic-label-${d.id}`}>{d.pic_name || "—"}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {canStage && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                              <button className="p-1.5 text-slate-400 hover:text-slate-900" data-testid={`deal-menu-${d.id}`} title={t("stage")}>
                                <MoveRight className="w-4 h-4" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                              <DropdownMenuLabel className="text-xs">{t("stage")}</DropdownMenuLabel>
                              {stageKeys.filter((x) => x !== d.stage).map((x) => (
                                <DropdownMenuItem key={x} onClick={() => moveStage(d.id, x)} data-testid={`move-${d.id}-${x}`}>
                                  <span className={`stage-dot ${cfg.stageStyle(x).dot} mr-2`}></span>{cfg.stageLabel(x)}
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                        {canDelete && (
                          <button
                            onClick={(e) => { e.stopPropagation(); del(d.id); }}
                            className="p-1.5 text-slate-400 hover:text-rose-600 ml-1"
                            data-testid={`delete-deal-${d.id}`}
                          ><Trash2 className="w-4 h-4" /></button>
                        )}
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className={`${editing && tab === "pnl" ? "max-w-5xl" : "max-w-2xl"} max-h-[90vh] overflow-y-auto crm-scroll`}>
          <DialogHeader>
            <DialogTitle>{dialogTitle()}</DialogTitle>
            <DialogDescription className="sr-only">{t("pipeline")}</DialogDescription>
          </DialogHeader>

          {editing && (
            <div className="flex gap-1 p-1 bg-slate-100 rounded-full w-fit" data-testid="deal-tabs">
              {["detail", "pnl"].map((k) => (
                <button key={k} data-testid={`deal-tab-${k}`} onClick={() => setTab(k)}
                  className={`px-4 py-1.5 text-xs font-semibold rounded-full transition-colors ${tab === k ? "bg-white shadow text-slate-900" : "text-slate-500 hover:text-slate-800"}`}>
                  {k === "pnl" && <Wallet className="w-3 h-3 inline mr-1" />}
                  {t(k === "detail" ? "detail_tab" : "pnl")}
                </button>
              ))}
            </div>
          )}

          {(!editing || tab === "detail") && (
            <div className="grid grid-cols-2 gap-3">
              <DealFormFields
                form={form}
                setForm={setForm}
                clients={clients}
                canWrite={canWrite}
                canStage={canStage}
                needsApproval={needsApproval}
                editing={editing}
              />
              {editing && <ProgressLog dealId={editing.id} />}
            </div>
          )}

          {editing && tab === "pnl" && (
            <div className="grid grid-cols-2 gap-3">
              <PnlPanel
                deal={editing}
                onSaved={(updated) => {
                  setEditing(updated);
                  setDeals((prev) => prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)));
                }}
              />
            </div>
          )}

          {editing && tab === "detail" && <DealComments deal={editing} />}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            {(canWrite || canStage) && (!editing || tab === "detail") && (
              <Button onClick={submit} data-testid="save-deal-button" className="bg-orange-500 hover:bg-orange-600">{t("save")}</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Pipeline;
